// Fotos y firma del borrador de la inspección, en IndexedDB.
//
// El borrador de texto vive en localStorage (lib/borrador.js); los Blobs no
// caben ahí. Aquí se guarda UN solo registro, ligado a la misma fecha (hora de
// Costa Rica) del borrador: fotos por ítem, fotos generales y la firma. Base
// propia (`cables-borrador`), aparte de la cola offline (`cables-offline`): un
// fallo o una limpieza aquí no toca inspecciones pendientes de enviar.
//
// Un registro por espacio, igual que el borrador de texto: canopy usa la clave
// de siempre ('actual'); el Tarzan Swing y la corrección, la suya.
//
// Los Blobs se guardan como ArrayBuffer + tipo (no como Blob): es lo que todos
// los navegadores clonan igual, incluido Safari viejo y el modo privado.
//
// Ninguna función lanza. Si IndexedDB no existe, falla o no hay cuota, la
// inspección sigue en memoria y queda una marca en localStorage para que, al
// restaurar, el asistente avise que las fotos y la firma no se recuperaron.
import { openDB } from 'idb';

const DB = 'cables-borrador';
const STORE = 'media';
const CLAVE = 'actual';
export const CLAVE_MEDIA_ERROR = 'cables-borrador-media-error:v1';
const sufijo = (espacio) => (!espacio || espacio === 'canopy' ? '' : `:${espacio}`);
const claveDe = (espacio) => `${CLAVE}${sufijo(espacio)}`;
const claveError = (espacio) => `${CLAVE_MEDIA_ERROR}${sufijo(espacio)}`;
// Tope del registro. Las fotos ya llegan comprimidas (1600 px, JPEG 0.7, unos
// 200 a 500 KB cada una); 40 MB es holgado para una inspección y evita llenar
// el almacenamiento del teléfono con un borrador.
export const LIMITE_BYTES = 40 * 1024 * 1024;

let dbPromesa = null;
const db = () => {
  if (!dbPromesa) {
    dbPromesa = openDB(DB, 1, {
      upgrade(d) {
        if (!d.objectStoreNames.contains(STORE)) d.createObjectStore(STORE);
      },
    }).catch((e) => {
      dbPromesa = null;
      throw e;
    });
  }
  return dbPromesa;
};

// Todas las operaciones van en fila, en el orden en que se piden: leer los
// Blobs es asíncrono, y sin la fila un borrar podía quedar pisado por una
// escritura que empezó antes.
let fila = Promise.resolve();
const enFila = (op) => {
  const p = fila.then(op, op);
  fila = p.catch(() => {});
  return p;
};

// Solo para pruebas: olvidar la conexión (p. ej. tras reemplazar indexedDB).
export const _reiniciarConexion = () => {
  dbPromesa = null;
};

const marcarError = (hay, espacio) => {
  try {
    if (hay) localStorage.setItem(claveError(espacio), '1');
    else localStorage.removeItem(claveError(espacio));
  } catch (e) { /* sin localStorage: no hay borrador que restaurar tampoco */ }
};

export const huboErrorMedia = (espacio) => {
  try {
    return localStorage.getItem(claveError(espacio)) === '1';
  } catch (e) {
    return false;
  }
};

const esBlob = (b) => typeof Blob !== 'undefined' && b instanceof Blob;
const soloBlobs = (xs) => (Array.isArray(xs) ? xs.filter(esBlob) : []);

const normalizar = ({ items, fotos, firma }) => {
  const its = {};
  Object.entries(items && typeof items === 'object' ? items : {}).forEach(([k, xs]) => {
    const bs = soloBlobs(xs);
    if (bs.length) its[k] = bs;
  });
  return { items: its, fotos: soloBlobs(fotos), firma: esBlob(firma) ? firma : null };
};

const tamano = ({ items, fotos, firma }) =>
  Object.values(items).flat().concat(fotos, firma ? [firma] : []).reduce((s, b) => s + (b.size || 0), 0);

const aDato = async (b) => ({ tipo: b.type || '', datos: await b.arrayBuffer() });
const esDato = (x) => !!x && typeof x === 'object' && !!x.datos && typeof x.datos.byteLength === 'number';
// Se copia a un Uint8Array: el ArrayBuffer clonado puede venir de otro reino.
const aBlob = (x) => new Blob([new Uint8Array(x.datos)], { type: typeof x.tipo === 'string' ? x.tipo : '' });
const blobsDe = (xs) => (Array.isArray(xs) ? xs.filter(esDato).map(aBlob) : []);

async function borrarRegistro(espacio) {
  try {
    const d = await db();
    await d.delete(STORE, claveDe(espacio));
  } catch (e) { /* sin IndexedDB: no hay nada que borrar */ }
}

// Guarda (reemplaza) las fotos y la firma del borrador de `fecha`.
// Devuelve true si quedaron guardadas; false si no (y deja la marca de error).
export function guardarMedia({ fecha, items, fotos, firma, espacio }) {
  const m = normalizar({ items, fotos, firma });
  return enFila(async () => {
    if (tamano(m) > LIMITE_BYTES) {
      marcarError(true, espacio);
      // Lo que había ya no corresponde a la inspección: mejor nada que algo viejo.
      await borrarRegistro(espacio);
      return false;
    }
    try {
      const its = {};
      for (const [k, bs] of Object.entries(m.items)) its[k] = await Promise.all(bs.map(aDato));
      const registro = {
        fecha,
        items: its,
        fotos: await Promise.all(m.fotos.map(aDato)),
        firma: m.firma ? await aDato(m.firma) : null,
        at: Date.now(),
      };
      const d = await db();
      await d.put(STORE, registro, claveDe(espacio));
      marcarError(false, espacio);
      return true;
    } catch (e) {
      marcarError(true, espacio);
      return false;
    }
  });
}

// Borra las fotos y la firma del borrador, y la marca de error.
export function borrarMedia(espacio) {
  return enFila(async () => {
    marcarError(false, espacio);
    await borrarRegistro(espacio);
  });
}

// Lee las fotos y la firma del borrador de `hoy`.
//   { ok: true, media: { items, fotos, firma } }  lo que había (puede ser vacío)
//   { ok: true, media: null }                    nada guardado para hoy
//   { ok: false }                                no se pudo leer
// Un registro de otro día se borra.
export function leerMedia(hoy, espacio) {
  return enFila(async () => {
    let r;
    try {
      const d = await db();
      r = await d.get(STORE, claveDe(espacio));
    } catch (e) {
      return { ok: false };
    }
    if (!r) return { ok: true, media: null };
    if (r.fecha !== hoy) {
      marcarError(false, espacio);
      await borrarRegistro(espacio);
      return { ok: true, media: null };
    }
    const items = {};
    Object.entries(r.items && typeof r.items === 'object' ? r.items : {}).forEach(([k, xs]) => {
      const bs = blobsDe(xs);
      if (bs.length) items[k] = bs;
    });
    return {
      ok: true,
      media: { items, fotos: blobsDe(r.fotos), firma: esDato(r.firma) ? aBlob(r.firma) : null },
    };
  });
}
